import { useCallback, useEffect, useState } from "react";

import {
  AiCostPlaneValue,
  AiReportRange,
  AiUsageFilter,
  AiUsageSummary,
  aiGatewayService,
} from "services/aiGatewayService";

/**
 * Loads the usage attribution report (WP-70) for a window, a plane and an optional scope.
 *
 * Kept apart from `useAiGateway` for the same reason as `useAiCosts`: the report aggregates the
 * largest table a tenant has, and it must only run once a Usage tab or a details section is
 * actually open. `days = 0` is the current calendar month.
 *
 * The filter object is compared by its two ids, not by identity, so callers can pass a literal.
 * `refreshToken` re-runs the same query without remounting the panel.
 */
export function useAiUsage(
  days: number,
  plane: AiCostPlaneValue,
  filter?: AiUsageFilter,
  refreshToken = 0,
  range?: AiReportRange,
) {
  const startDate = range?.startDate ?? null;
  const endDate = range?.endDate ?? null;
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const customerCompanyId = filter?.customerCompanyId ?? null;
  const customerId = filter?.customerId ?? null;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await aiGatewayService.getUsage(
        days,
        plane,
        { customerCompanyId, customerId },
        startDate && endDate ? { startDate, endDate } : undefined,
      );
      setSummary(loaded);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "load-failed");
    } finally {
      setLoading(false);
    }
    // `refreshToken` is not read here — it is the dependency that re-runs the same query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    days,
    plane,
    customerCompanyId,
    customerId,
    startDate,
    endDate,
    refreshToken,
  ]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { summary, loading, error, reload };
}
